import { Component, Input, OnChanges, OnDestroy, OnInit, inject } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Router, RouterLink } from '@angular/router';
import {
  changeEditCommentMode,
  createReplyToCommentData,
} from 'src/app/data/store/actions/posts.actions';
import { selectCurrentUser } from 'src/app/data/store/selectors/users.selectors';
import { befreeUrlValues } from '../../constants';
import { CurrentPlatform, TextSizes, TextType } from '../../enums';
import { PostsService } from '../../services/posts/posts.service';
import { IComment } from '../../services/posts/posts.type';
import {
  selectReplyTo,
  selectCommentEditMode,
  selectEditedCommentData,
} from '../../../data/store/selectors/posts.selectors';
import { selectCurrentPlatform } from 'src/app/data/store/selectors/platform.selectors';
import { AsyncPipe } from '@angular/common';
import { ImageComponent } from '../image/image.component';
import { IonicModule } from '@ionic/angular';
import { TextComponent } from '../text/text.component';
import { DateFormatPipe } from '../../pipe/date-format/date-format.pipe';
import { AddBaseUrlPipe } from '../../pipe/add-baseUrl/add-base-url.pipe';
import { GetFullNamePipe } from '../../pipe/get-full-name/get-full-name.pipe';
import { GetInnerHtmlPipe } from '../../pipe/get-inner-html/get-inner-html.pipe';
import { YearsOfServiceBadgeIconPipe } from '../../pipe/years-of-service-badge-icon/years-of-service-badge-icon.pipe';
import { SvgComponent } from '../svg/svg.component';

@UntilDestroy()
@Component({
  selector: 'app-post-comment',
  templateUrl: './post-comment.component.html',
  styleUrls: ['./post-comment.component.scss'],
  imports: [
    ImageComponent,
    IonicModule,
    RouterLink,
    SvgComponent,
    TextComponent,
    AsyncPipe,
    DateFormatPipe,
    AddBaseUrlPipe,
    GetFullNamePipe,
    GetInnerHtmlPipe,
    YearsOfServiceBadgeIconPipe,
  ],
})
export class PostCommentComponent implements OnInit, OnChanges, OnDestroy {
  private _store = inject(Store);
  private _router = inject(Router);
  private _postService = inject(PostsService);

  @Input() comment: IComment | null = null;

  @Input() newComment: IComment | null = null;

  @Input() replyLevel = 0;

  @Input() editCommentId: number | null = null;

  @Input() preventScrollToReplies = false;

  @Input() changeCommentEditMode: ((comment: IComment, editMode?: boolean) => void) | undefined;

  @Input() rerenderPhotoReportList?: VoidFunction;

  readonly Platform = CurrentPlatform;

  readonly TextType = TextType;

  readonly TextSizes = TextSizes;

  public replyCommentId = 0;

  public isActive = false;

  public repliesCount = 0;

  public replyComments: IComment[] = [];

  public platform$ = this._store.select(selectCurrentPlatform);

  public currentUser$ = this._store.select(selectCurrentUser);

  ngOnInit(): void {
    this.repliesCount = this.comment?.repliesCount ?? 0;
    this.replyComments = this.comment?.replies || [];

    this._store
      .select(selectReplyTo)
      .pipe(untilDestroyed(this))
      .subscribe((reply) => {
        this.replyCommentId = reply?.id || 0;
      });

    this._store
      .select(selectEditedCommentData)
      .pipe(untilDestroyed(this))
      .subscribe((editedComment) => {
        if (editedComment) {
          this.replyComments = this.replyComments.map((comment) => {
            return comment.id === editedComment.id ? editedComment : comment;
          });
        }
      });

    this._store
      .select(selectCommentEditMode)
      .pipe(untilDestroyed(this))
      .subscribe((isEditMode: boolean) => {
        if (!isEditMode) {
          this.editCommentId = null;
        }
      });
  }

  ngOnChanges(): void {
    if (this.newComment?.replayFor?.id === this.comment?.id && !this.editCommentId) {
      this.updateList(this.newComment!);
    }
  }

  ngOnDestroy(): void {
    this._store.dispatch(
      changeEditCommentMode({
        isEditMode: false,
        currentCommentData: {},
      }),
    );
  }

  get authorUrl() {
    return ['/', befreeUrlValues.dashboard, befreeUrlValues.employees, this.comment!.author.id];
  }

  get commentText() {
    if (!this.comment) {
      return '';
    }

    if (!this.comment.replayFor?.id) {
      return this.comment.text;
    }

    const commaIndex = this.comment.text.indexOf(', ');
    if (commaIndex === -1) {
      return this.comment.text;
    }

    const name = this.comment.text.slice(0, commaIndex);
    const message = this.comment.text.slice(commaIndex + 2);
    return `<span>${name}</span>, ${message}`;
  }

  public editComment() {
    if (!this.editCommentId) {
      this._store.dispatch(createReplyToCommentData({ comment: null }));
    }
    this.changeCommentEditMode?.(this.comment!);
  }

  public replyToComment() {
    if (this.replyCommentId === this.comment!.id) {
      this.changeCommentEditMode?.(this.comment!, false);
      this._store.dispatch(createReplyToCommentData({ comment: null }));
    } else {
      this.changeCommentEditMode?.(this.comment!, false);
      this._store.dispatch(createReplyToCommentData({ comment: this.comment! }));
    }
  }

  public updateList(comment: IComment) {
    const replyComments = [...this.replyComments];
    const index = replyComments.findIndex((reply) => reply.id === comment.id);

    if (index > -1) {
      replyComments[index] = comment;
      this.replyComments = replyComments;
    } else {
      this.repliesCount += 1;
      replyComments.push(comment);
      this.replyComments = replyComments;

      setTimeout(() => {
        this.scrollToComment(comment.id);
      });
    }
  }

  public onAuthorClick() {
    this.isActive = true;
  }

  public scrollToComment(id: number) {
    const el = document.querySelector(`[data-comment-id="${id}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  public trackRepliesBy(_index: number, comment: IComment) {
    return comment.id;
  }
}
